import { z } from 'zod';

import { richDocSchema, storedRichDocSchema } from './rich-text';
import { giftCodeViewSchema } from './store';

/**
 * Equipas de competicao (decidido pelo GrowuP a 24-10-2026; ver docs/equipas).
 * Parte 1: modalidades, jogos, equipas e os managers de cada equipa.
 * Parte 2: funcoes de jogo, epocas e o lineup.
 * Parte 5: calendario de treinos e jogos.
 * Parte 6: regulamento (corpo comum e parte de cada equipa) e aceitacoes.
 * Parte 7: deslocacoes a torneios, convocatorias e respostas.
 * Parte 8: ficha interna do jogador (so staff de equipas).
 * Parte 9: codigos de oferta da camisola da equipa.
 *
 * Quem gere:
 *   - `teams.manage` (Gestor de Equipas, Administrador geral): tudo;
 *   - `teams.own` (Manager de equipa): so as equipas que lhe forem atribuidas;
 *     nelas muda a apresentacao (descricao, logotipo, foto), as epocas e o
 *     lineup.
 *
 * Prefixo HTTP: `/api/site/teams/...`, back-office, so em gestao.
 */

const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue a imagem no back-office');

export const TEAM_DIVISION_KEYS = ['esports', 'tcg', 'other'] as const;
export const teamDivisionKeySchema = z.enum(TEAM_DIVISION_KEYS);
export type TeamDivisionKey = z.infer<typeof teamDivisionKeySchema>;

export const TEAM_KINDS = ['group', 'individual'] as const;
export const teamKindSchema = z.enum(TEAM_KINDS);
export type TeamKind = z.infer<typeof teamKindSchema>;
export const TEAM_KIND_LABELS: Record<TeamKind, string> = {
  group: 'Grupo',
  individual: 'Individual',
};
export const TEAM_KIND_HINTS: Record<TeamKind, string> = {
  group: 'Os jogadores competem juntos; o lineup tem titulares e suplentes.',
  individual: 'Cada jogador compete sozinho em nome da equipa.',
};

export const LINEUP_MODES = ['season', 'continuous'] as const;
export const lineupModeSchema = z.enum(LINEUP_MODES);
export type LineupMode = z.infer<typeof lineupModeSchema>;
export const LINEUP_MODE_LABELS: Record<LineupMode, string> = {
  season: 'Por época',
  continuous: 'Contínuo',
};

export const teamStatusSchema = z.enum(['active', 'archived']);
export type TeamStatus = z.infer<typeof teamStatusSchema>;

export const teamDivisionSchema = z.object({
  id: z.string().uuid(),
  key: teamDivisionKeySchema,
  name: z.string(),
  brandName: z.string(),
});
export type TeamDivision = z.infer<typeof teamDivisionSchema>;

export const teamGameSchema = z.object({
  id: z.string().uuid(),
  divisionId: z.string().uuid(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  /** Equipas (ativas e arquivadas) deste jogo: com equipas nao se apaga. */
  teamCount: z.number().int(),
  /** Funcoes de jogo (IGL, Entry, ...), por ordem alfabetica. */
  roles: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type TeamGame = z.infer<typeof teamGameSchema>;

/** Modalidades e jogos, para os filtros e os formularios. */
export const teamCatalogSchema = z.object({
  divisions: z.array(teamDivisionSchema),
  games: z.array(teamGameSchema),
});
export type TeamCatalog = z.infer<typeof teamCatalogSchema>;

export const teamDivisionInputSchema = z.object({
  brandName: z.string().trim().max(80).default(''),
});

export const teamGameInputSchema = z.object({
  divisionId: z.string().uuid(),
  name: z.string().trim().min(1, 'escreva o nome do jogo').max(60),
  logoUrl: mediaSrcSchema.nullable().default(null),
});
export type TeamGameInput = z.input<typeof teamGameInputSchema>;

export const teamInputSchema = z.object({
  name: z.string().trim().min(1, 'escreva o nome da equipa').max(80),
  gameId: z.string().uuid('escolha o jogo'),
  kind: teamKindSchema,
  lineupMode: lineupModeSchema,
  academy: z.boolean().default(false),
  brandName: z.string().trim().max(80).default(''),
  logoUrl: mediaSrcSchema.nullable().default(null),
  photoUrl: mediaSrcSchema.nullable().default(null),
  description: z.string().trim().max(600).default(''),
});
export type TeamInput = z.input<typeof teamInputSchema>;

/** O que o Manager de equipa pode mudar na sua equipa. */
export const teamPresentationInputSchema = teamInputSchema.pick({
  logoUrl: true,
  photoUrl: true,
  description: true,
});
export type TeamPresentationInput = z.input<typeof teamPresentationInputSchema>;

export const teamManagerSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
});
export type TeamManager = z.infer<typeof teamManagerSchema>;

export const adminTeamSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  gameId: z.string().uuid(),
  kind: teamKindSchema,
  lineupMode: lineupModeSchema,
  academy: z.boolean(),
  /** O nome de marca da propria equipa (vazio = o da modalidade). */
  brandName: z.string(),
  logoUrl: z.string().nullable(),
  photoUrl: z.string().nullable(),
  description: z.string(),
  status: teamStatusSchema,
  managers: z.array(teamManagerSchema),
  /** Parceiros da equipa, pela ordem da pagina (RF-EQ13). */
  partnerIds: z.array(z.string().uuid()),
  updatedAt: z.string(),
  updatedBy: z.string(),
});
export type AdminTeam = z.infer<typeof adminTeamSchema>;

/** A lista do back-office: o Manager de equipa so recebe as suas. */
export const teamListSchema = z.object({
  /** `true` para o Gestor de Equipas; `false` para um Manager de equipa. */
  canManageAll: z.boolean(),
  catalog: teamCatalogSchema,
  teams: z.array(adminTeamSchema),
  /** Parceiros publicados, para escolher os de cada equipa (so para o Gestor). */
  partners: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type TeamList = z.infer<typeof teamListSchema>;

export const teamGameRoleInputSchema = z.object({
  name: z.string().trim().min(1, 'escreva a função').max(40),
});

export const teamPartnersInputSchema = z.object({
  partnerIds: z.array(z.string().uuid()).max(20),
});

export const teamManagerInputSchema = z.object({ accountId: z.string().uuid() });

export const teamAccountSchema = z.object({ id: z.string().uuid(), login: z.string() });
export type TeamAccount = z.infer<typeof teamAccountSchema>;

/** A marca que aparece: a da equipa ou, se vazia, a da modalidade. */
export function teamBrand(team: { brandName: string }, division: { brandName: string }): string {
  return team.brandName || division.brandName;
}

/**
 * O endereco da pagina publica a partir do nome: sem acentos, minusculas,
 * hifenes. `taken` sao os que ja existem; repete-se com -2, -3, ...
 */
export function teamSlug(name: string, taken: ReadonlySet<string> = new Set()): string {
  const base =
    name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 70)
      .replace(/-+$/g, '') || 'equipa';
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${String(n)}`;
    if (!taken.has(candidate)) return candidate;
  }
}

// ---------------------------------------------------------------------------
// Parte 2: epocas e lineup
// ---------------------------------------------------------------------------

/** Funcao na equipa. `substitute` so nas equipas de grupo. */
export const TEAM_MEMBER_ROLES = [
  'captain',
  'player',
  'substitute',
  'coach',
  'manager',
  'analyst',
] as const;
export const teamMemberRoleSchema = z.enum(TEAM_MEMBER_ROLES);
export type TeamMemberRole = z.infer<typeof teamMemberRoleSchema>;
export const TEAM_MEMBER_ROLE_LABELS: Record<TeamMemberRole, string> = {
  captain: 'Capitão',
  player: 'Jogador',
  substitute: 'Suplente',
  coach: 'Treinador',
  manager: 'Manager',
  analyst: 'Analista',
};

/** As funcoes que uma equipa deste tipo pode ter. */
export function teamMemberRoles(kind: TeamKind): readonly TeamMemberRole[] {
  return kind === 'group' ? TEAM_MEMBER_ROLES : TEAM_MEMBER_ROLES.filter((r) => r !== 'substitute');
}

const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'escolha a data');

export const teamSeasonSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
});
export type TeamSeason = z.infer<typeof teamSeasonSchema>;

export const teamSeasonInputSchema = z
  .object({
    name: z.string().trim().min(1, 'escreva o nome da época').max(40),
    startsOn: daySchema,
    endsOn: daySchema,
    /** Ao criar: copia quem estava na epoca indicada (e nao saiu). */
    copyFrom: z.string().uuid().nullable().default(null),
  })
  .refine((s) => s.startsOn <= s.endsOn, {
    message: 'o fim tem de ser depois do início',
    path: ['endsOn'],
  });
export type TeamSeasonInput = z.input<typeof teamSeasonInputSchema>;

export const teamMemberSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  login: z.string(),
  /** Conta de jovem da Arena (menor, sem email). */
  youth: z.boolean(),
  seasonId: z.string().uuid().nullable(),
  role: teamMemberRoleSchema,
  gameRoleId: z.string().uuid().nullable(),
  jerseyNumber: z.number().int().nullable(),
  joinedOn: z.string(),
  leftOn: z.string().nullable(),
  /** Pode apagar-se por engano so nas primeiras 24 horas. */
  deletable: z.boolean(),
  /**
   * Jovem: o encarregado autorizou competir e aparecer em publico (RN-EQ3).
   * Sem ela a linha fica "pendente" e nao aparece no site. `null` = adulto.
   */
  competeAuthorized: z.boolean().nullable(),
  /** Jovem: o encarregado autorizou a foto nas paginas das equipas (RN-EQ13). `null` = adulto. */
  photoAuthorized: z.boolean().nullable(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamLineupSchema = z.object({
  seasons: z.array(teamSeasonSchema),
  /** Todas as linhas (atuais e historico), de todas as epocas. */
  members: z.array(teamMemberSchema),
});
export type TeamLineup = z.infer<typeof teamLineupSchema>;

const memberFields = {
  role: teamMemberRoleSchema,
  gameRoleId: z.string().uuid().nullable().default(null),
  jerseyNumber: z.number().int().min(0).max(99).nullable().default(null),
  joinedOn: daySchema,
};

export const teamMemberInputSchema = z.object({
  accountId: z.string().uuid('escolha a conta'),
  seasonId: z.string().uuid().nullable().default(null),
  ...memberFields,
});
export type TeamMemberInput = z.input<typeof teamMemberInputSchema>;

export const teamMemberUpdateSchema = z.object(memberFields);
export type TeamMemberUpdate = z.input<typeof teamMemberUpdateSchema>;

export const teamMemberLeaveSchema = z.object({ leftOn: daySchema });

/** Contas para o lineup: tambem de jovens (membros da Arena). */
export const teamLineupAccountSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  youth: z.boolean(),
});
export type TeamLineupAccount = z.infer<typeof teamLineupAccountSchema>;

/** Atuais primeiro (capitao, jogadores, ...), depois o historico por data de saida. */
export function compareTeamMembers(a: TeamMember, b: TeamMember): number {
  if ((a.leftOn === null) !== (b.leftOn === null)) return a.leftOn === null ? -1 : 1;
  if (a.leftOn !== null && b.leftOn !== null && a.leftOn !== b.leftOn) {
    return a.leftOn < b.leftOn ? 1 : -1;
  }
  return (
    TEAM_MEMBER_ROLES.indexOf(a.role) - TEAM_MEMBER_ROLES.indexOf(b.role) ||
    a.login.localeCompare(b.login, 'pt')
  );
}

// ---- Calendario (parte 5) ---------------------------------------------------------

/** Treinos e jogos (RF-EQ17). Os jogos aparecem em publico; os treinos nunca. */
export const TEAM_EVENT_KINDS = ['training', 'match'] as const;
export const teamEventKindSchema = z.enum(TEAM_EVENT_KINDS);
export type TeamEventKind = z.infer<typeof teamEventKindSchema>;

export const TEAM_EVENT_KIND_LABELS: Record<TeamEventKind, string> = {
  training: 'Treino',
  match: 'Jogo',
};

/** Repetir um treino todas as semanas, ate um ano. */
export const MAX_TEAM_EVENT_REPEATS = 52;

const eventTimeSchema = z.string().datetime({ offset: true, message: 'indique a data e a hora' });

/** Um treino ou jogo no back-office. */
export const teamEventSchema = z.object({
  id: z.string().uuid(),
  kind: teamEventKindSchema,
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  online: z.boolean(),
  location: z.string(),
  opponent: z.string(),
  competition: z.string(),
  /** Para quem esta na equipa; nunca em publico. */
  notes: z.string(),
  cancelled: z.boolean(),
});
export type TeamEvent = z.infer<typeof teamEventSchema>;

export const teamCalendarSchema = z.object({
  /** Do mais proximo para o mais distante, cancelados incluidos. */
  upcoming: z.array(teamEventSchema),
  /** Os ultimos 20, do mais recente para o mais antigo. */
  past: z.array(teamEventSchema),
});
export type TeamCalendar = z.infer<typeof teamCalendarSchema>;

const eventFields = z.object({
  kind: teamEventKindSchema,
  startsAt: eventTimeSchema,
  endsAt: eventTimeSchema.nullable().default(null),
  online: z.boolean().default(false),
  location: z.string().trim().max(200).default(''),
  opponent: z.string().trim().max(120).default(''),
  competition: z.string().trim().max(120).default(''),
  notes: z.string().trim().max(2000).default(''),
});

const endsAfterStart = (e: { startsAt: string; endsAt: string | null }) =>
  e.endsAt === null || new Date(e.endsAt) > new Date(e.startsAt);
const endsAfterStartMessage = { message: 'o fim tem de ser depois do início', path: ['endsAt'] };

export const teamEventInputSchema = eventFields
  .extend({
    /** So ao criar: mais N vezes, uma por semana, a mesma hora. */
    repeatWeeks: z.number().int().min(0).max(MAX_TEAM_EVENT_REPEATS).default(0),
  })
  .refine(endsAfterStart, endsAfterStartMessage);
export type TeamEventInput = z.input<typeof teamEventInputSchema>;

export const teamEventUpdateSchema = eventFields.refine(endsAfterStart, endsAfterStartMessage);
export type TeamEventUpdate = z.input<typeof teamEventUpdateSchema>;

/** Um proximo jogo na pagina publica: sem notas. */
export const publicTeamMatchSchema = z.object({
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  online: z.boolean(),
  location: z.string(),
  opponent: z.string(),
  competition: z.string(),
});
export type PublicTeamMatch = z.infer<typeof publicTeamMatchSchema>;

// ---- Regulamento (parte 6) --------------------------------------------------------

/** Uma versao publicada (RF-EQ14): nunca se apaga nem se altera. */
export const teamRulesVersionSummarySchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  publishedAt: z.string(),
  /** Login de quem publicou. */
  publishedBy: z.string(),
});
export type TeamRulesVersionSummary = z.infer<typeof teamRulesVersionSummarySchema>;

export const teamRulesVersionSchema = teamRulesVersionSummarySchema.extend({
  body: storedRichDocSchema,
});
export type TeamRulesVersion = z.infer<typeof teamRulesVersionSchema>;

/** O corpo comum a todas as equipas, no back-office. */
export const teamCommonRulesSchema = z.object({
  current: teamRulesVersionSchema.nullable(),
  /** Da mais recente para a mais antiga. */
  history: z.array(teamRulesVersionSummarySchema),
});
export type TeamCommonRules = z.infer<typeof teamCommonRulesSchema>;

export const TEAM_RULES_STATUSES = ['none', 'pending', 'accepted'] as const;
export const teamRulesStatusSchema = z.enum(TEAM_RULES_STATUSES);
/** `none`: ainda nao ha regulamento; `pending`: falta aceitar a versao atual. */
export type TeamRulesStatus = z.infer<typeof teamRulesStatusSchema>;

/** O regulamento de uma equipa no back-office, com quem do lineup atual ja aceitou. */
export const teamRulesSchema = z.object({
  common: teamRulesVersionSchema.nullable(),
  team: teamRulesVersionSchema.nullable(),
  /** As versoes da parte da equipa, da mais recente para a mais antiga. */
  history: z.array(teamRulesVersionSummarySchema),
  members: z.array(
    z.object({
      accountId: z.string().uuid(),
      login: z.string(),
      youth: z.boolean(),
      status: teamRulesStatusSchema,
      /** A ultima aceitacao (pode ser de uma versao anterior). */
      acceptedAt: z.string().nullable(),
      /** Quem aceitou foi o encarregado. */
      byGuardian: z.boolean(),
    }),
  ),
});
export type TeamRules = z.infer<typeof teamRulesSchema>;

/** Publicar uma versao nova (do corpo comum ou da parte da equipa). */
export const teamRulesInputSchema = z.object({ body: richDocSchema });
export type TeamRulesInput = z.input<typeof teamRulesInputSchema>;

/** O regulamento para quem esta na equipa (ou o seu encarregado) ler e aceitar. */
export const memberTeamRulesSchema = z.object({
  teamName: z.string(),
  status: teamRulesStatusSchema,
  common: teamRulesVersionSchema.nullable(),
  team: teamRulesVersionSchema.nullable(),
  acceptedAt: z.string().nullable(),
});
export type MemberTeamRules = z.infer<typeof memberTeamRulesSchema>;

/** Aceita-se exatamente o que se leu: se entretanto mudou, a API recusa. */
export const teamRulesAcceptSchema = z.object({
  commonVersionId: z.string().uuid().nullable(),
  teamVersionId: z.string().uuid().nullable(),
});
export type TeamRulesAccept = z.infer<typeof teamRulesAcceptSchema>;

// ---- Deslocacoes (parte 7) --------------------------------------------------------

/**
 * A resposta de um convocado (RF-EQ11): um adulto diz "vou / nao vou"; por um
 * jovem, o encarregado autoriza ou recusa.
 */
export const TEAM_TRIP_ANSWERS = [
  'pending',
  'going',
  'not_going',
  'authorized',
  'refused',
] as const;
export const teamTripAnswerSchema = z.enum(TEAM_TRIP_ANSWERS);
export type TeamTripAnswer = z.infer<typeof teamTripAnswerSchema>;

export const TEAM_TRIP_ANSWER_LABELS: Record<TeamTripAnswer, string> = {
  pending: 'Sem resposta',
  going: 'Vai',
  not_going: 'Não vai',
  authorized: 'Autorizado',
  refused: 'Não autorizado',
};

export const teamTripMemberSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  youth: z.boolean(),
  answer: teamTripAnswerSchema,
  answeredAt: z.string().nullable(),
});
export type TeamTripMember = z.infer<typeof teamTripMemberSchema>;

/** Uma deslocacao no back-office. */
export const teamTripSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  location: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  details: z.string(),
  cancelled: z.boolean(),
  members: z.array(teamTripMemberSchema),
});
export type TeamTrip = z.infer<typeof teamTripSchema>;

export const teamTripsSchema = z.object({
  /** As que ainda nao acabaram, da mais proxima para a mais distante. */
  upcoming: z.array(teamTripSchema),
  /** As ultimas 10, da mais recente para a mais antiga. */
  past: z.array(teamTripSchema),
});
export type TeamTrips = z.infer<typeof teamTripsSchema>;

export const teamTripInputSchema = z
  .object({
    title: z.string().trim().min(1, 'indique o torneio').max(120),
    location: z.string().trim().max(200).default(''),
    startsAt: eventTimeSchema,
    endsAt: eventTimeSchema,
    details: z.string().trim().max(2000).default(''),
    /** Quem vai: a lista completa (ao editar, quem sai da lista deixa de estar convocado). */
    accountIds: z.array(z.string().uuid()).min(1, 'convoque pelo menos uma pessoa').max(60),
  })
  .refine((t) => new Date(t.endsAt) > new Date(t.startsAt), {
    message: 'o regresso tem de ser depois da partida',
    path: ['endsAt'],
  });
export type TeamTripInput = z.input<typeof teamTripInputSchema>;

/** Uma deslocacao para quem foi convocado (ou o seu encarregado). */
export const memberTripSchema = z.object({
  id: z.string().uuid(),
  teamSlug: z.string(),
  teamName: z.string(),
  title: z.string(),
  location: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  details: z.string(),
  cancelled: z.boolean(),
  answer: teamTripAnswerSchema,
  /** Ja passou a partida: nao se pode mudar a resposta. */
  closed: z.boolean(),
});
export type MemberTrip = z.infer<typeof memberTripSchema>;

/** `true`: vou (adulto) ou autorizo (encarregado). */
export const teamTripAnswerInputSchema = z.object({ going: z.boolean() });

// ---- Ficha interna (parte 8) ------------------------------------------------------

/** Onde a pessoa tem conta de jogo (RF-EQ18). */
export const PLAYER_PLATFORMS = [
  'riot',
  'ea',
  'steam',
  'epic',
  'battlenet',
  'psn',
  'xbox',
  'nintendo',
  'ptcgl',
  'other',
] as const;
export const playerPlatformSchema = z.enum(PLAYER_PLATFORMS);
export type PlayerPlatform = z.infer<typeof playerPlatformSchema>;

export const PLAYER_PLATFORM_LABELS: Record<PlayerPlatform, string> = {
  riot: 'Riot ID',
  ea: 'EA ID',
  steam: 'Steam',
  epic: 'Epic Games',
  battlenet: 'Battle.net',
  psn: 'PlayStation',
  xbox: 'Xbox',
  nintendo: 'Nintendo',
  ptcgl: 'Pokémon TCG Live',
  other: 'Outra',
};

export const TEAM_AVAILABILITY_DAYS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'] as const;
export const TEAM_AVAILABILITY_DAY_LABELS: Record<(typeof TEAM_AVAILABILITY_DAYS)[number], string> =
  { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sáb', dom: 'Dom' };
export const TEAM_AVAILABILITY_PERIODS = ['manha', 'tarde', 'noite'] as const;
export const TEAM_AVAILABILITY_PERIOD_LABELS: Record<
  (typeof TEAM_AVAILABILITY_PERIODS)[number],
  string
> = { manha: 'Manhã', tarde: 'Tarde', noite: 'Noite' };
/** Dia e periodo, por exemplo `seg-noite`. */
export const TEAM_AVAILABILITY_SLOTS = TEAM_AVAILABILITY_DAYS.flatMap((d) =>
  TEAM_AVAILABILITY_PERIODS.map((p) => `${d}-${p}` as const),
);
export const teamAvailabilitySlotSchema = z.enum(
  TEAM_AVAILABILITY_SLOTS as [
    (typeof TEAM_AVAILABILITY_SLOTS)[number],
    ...(typeof TEAM_AVAILABILITY_SLOTS)[number][],
  ],
);

export const playerGameAccountSchema = z.object({
  platform: playerPlatformSchema,
  handle: z.string().trim().min(1).max(80),
});
export type PlayerGameAccount = z.infer<typeof playerGameAccountSchema>;

export const teamPlayerNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  /** Quem escreveu pode apagar. */
  mine: z.boolean(),
});
export type TeamPlayerNote = z.infer<typeof teamPlayerNoteSchema>;

/** A ficha de um jogador numa equipa: so para o Gestor e o Manager da equipa. */
export const teamPlayerSheetSchema = z.object({
  accountId: z.string(),
  login: z.string(),
  youth: z.boolean(),
  /** Ainda esta no lineup (as fichas de quem saiu ficam guardadas). */
  current: z.boolean(),
  gameAccounts: z.array(playerGameAccountSchema),
  rank: z.string(),
  gameRoleId: z.string().nullable(),
  availability: z.array(teamAvailabilitySlotSchema),
  notes: z.array(teamPlayerNoteSchema),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type TeamPlayerSheet = z.infer<typeof teamPlayerSheetSchema>;

export const teamPlayerSheetsSchema = z.object({ players: z.array(teamPlayerSheetSchema) });
export type TeamPlayerSheets = z.infer<typeof teamPlayerSheetsSchema>;

export const teamPlayerSheetInputSchema = z.object({
  rank: z.string().trim().max(60),
  gameRoleId: z.string().uuid().nullable(),
  availability: z.array(teamAvailabilitySlotSchema).max(TEAM_AVAILABILITY_SLOTS.length),
  gameAccounts: z
    .array(playerGameAccountSchema)
    .max(PLAYER_PLATFORMS.length)
    .refine((list) => new Set(list.map((a) => a.platform)).size === list.length, {
      message: 'uma conta por plataforma',
    }),
});
export type TeamPlayerSheetInput = z.infer<typeof teamPlayerSheetInputSchema>;

export const teamPlayerNoteInputSchema = z.object({ text: z.string().trim().min(1).max(2000) });

// ---- Camisola da equipa (parte 9) -------------------------------------------------

/** Quem esta no lineup atual e o codigo da camisola desta equipa, se ja o tem. */
export const teamShirtPlayerSchema = z.object({
  accountId: z.string(),
  login: z.string(),
  youth: z.boolean(),
  role: teamMemberRoleSchema,
  gift: giftCodeViewSchema.nullable(),
});
export type TeamShirtPlayer = z.infer<typeof teamShirtPlayerSchema>;

export const teamShirtsSchema = z.object({
  /** O produto que os codigos oferecem; `null` enquanto o Gestor nao o escolhe. */
  product: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** So o Gestor de Equipas escolhe o produto (lista vazia para o Manager). */
  products: z.array(z.object({ id: z.string(), name: z.string(), active: z.boolean() })),
  players: z.array(teamShirtPlayerSchema),
  /** Codigos de quem ja saiu do lineup (ficam validos). */
  former: z.array(teamShirtPlayerSchema),
});
export type TeamShirts = z.infer<typeof teamShirtsSchema>;

export const teamShirtProductInputSchema = z.object({ productId: z.string().uuid().nullable() });

// ---- Paginas publicas (parte 4) ---------------------------------------------------

/** Uma equipa na lista de `growup.gg/equipas`. */
export const publicTeamSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  /** A marca que aparece (a da equipa ou a da modalidade). */
  brand: z.string(),
  academy: z.boolean(),
  kind: teamKindSchema,
  logoUrl: z.string().nullable(),
  players: z.number().int(),
});
export type PublicTeamSummary = z.infer<typeof publicTeamSummarySchema>;

/** Por modalidade e depois por jogo (RN-EQ14); so modalidades e jogos com equipas. */
export const publicTeamDirectorySchema = z.object({
  divisions: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      brandName: z.string(),
      games: z.array(
        z.object({
          name: z.string(),
          logoUrl: z.string().nullable(),
          teams: z.array(publicTeamSummarySchema),
        }),
      ),
    }),
  ),
  /** Ha antigas equipas (mostra a ligacao para `/equipas/antigas`). */
  hasArchived: z.boolean(),
});
export type PublicTeamDirectory = z.infer<typeof publicTeamDirectorySchema>;

/**
 * Uma pessoa no lineup publico: so alcunha e funcao (RN-EQ6). A foto vem so
 * de adultos ou com a autorizacao do encarregado (RN-EQ13).
 */
export const publicTeamPlayerSchema = z.object({
  id: z.string().uuid(),
  nickname: z.string(),
  level: z.enum(['user', 'member', 'associate']),
  role: teamMemberRoleSchema,
  gameRole: z.string().nullable(),
  jerseyNumber: z.number().int().nullable(),
  photoUrl: z.string().nullable(),
  /** Canal da Twitch, se for streamer da Grow uP (RF-EQ16). */
  twitchChannel: z.string().nullable(),
  /** A pagina publica da pessoa (`/membros/<nickname>`), se a ativou. */
  pageUrl: z.string().nullable().default(null),
});
export type PublicTeamPlayer = z.infer<typeof publicTeamPlayerSchema>;

export const publicTeamSchema = z.object({
  slug: z.string(),
  name: z.string(),
  brand: z.string(),
  academy: z.boolean(),
  kind: teamKindSchema,
  division: z.string(),
  game: z.string(),
  gameLogoUrl: z.string().nullable(),
  logoUrl: z.string().nullable(),
  photoUrl: z.string().nullable(),
  description: z.string(),
  archived: z.boolean(),
  /** A epoca mostrada (equipas por epoca); `null` no lineup continuo. */
  season: z.string().nullable(),
  players: z.array(publicTeamPlayerSchema),
  partners: z.array(
    z.object({ name: z.string(), logoUrl: z.string().nullable(), websiteUrl: z.string() }),
  ),
  /** Parte 5: os proximos jogos (no maximo 5); vazio nas antigas equipas. */
  matches: z.array(publicTeamMatchSchema),
});
export type PublicTeam = z.infer<typeof publicTeamSchema>;

// ---- As minhas equipas (parte 4b) -------------------------------------------------

/** Uma linha do lineup de uma pessoa, para o perfil e a ficha do socio (RF-EQ5). */
export const memberTeamSchema = z.object({
  slug: z.string(),
  name: z.string(),
  game: z.string(),
  role: teamMemberRoleSchema,
  gameRole: z.string().nullable(),
  jerseyNumber: z.number().int().nullable(),
  season: z.string().nullable(),
  joinedOn: z.string(),
  leftOn: z.string().nullable(),
  /** A equipa foi arquivada. */
  archived: z.boolean(),
  /** Jovem sem a autorizacao do encarregado para competir: o lugar esta pendente. */
  pending: z.boolean(),
  /** Parte 6: o regulamento da equipa (so nas atuais; nas anteriores e `none`). */
  rules: teamRulesStatusSchema,
});
export type MemberTeam = z.infer<typeof memberTeamSchema>;

/** Parte 5: um treino ou jogo das equipas atuais, para quem esta nelas. */
export const memberTeamEventSchema = teamEventSchema.omit({ id: true }).extend({
  teamSlug: z.string(),
  teamName: z.string(),
});
export type MemberTeamEvent = z.infer<typeof memberTeamEventSchema>;

export const memberTeamsSchema = z.object({
  current: z.array(memberTeamSchema),
  past: z.array(memberTeamSchema),
  /** Parte 5: os proximos treinos e jogos (14 dias), cancelados incluidos. */
  upcoming: z.array(memberTeamEventSchema),
  /** Parte 7: as deslocacoes para que esta convocado e que ainda nao acabaram. */
  trips: z.array(memberTripSchema),
});
export type MemberTeams = z.infer<typeof memberTeamsSchema>;
